import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, MessageCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    smsOptIn: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success("Message sent successfully!");
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Visit Us",
      lines: ["738 Longfellow St NW", "Unit 107", "Washington, DC"],
    },
    {
      icon: Phone,
      title: "Call Us",
      lines: ["(202) 704-2459", "Open Daily: 9AM-6PM"],
      link: "tel:+12027042459",
    },
    {
      icon: MessageCircle,
      title: "WhatsApp",
      lines: ["(202) 704-2459", "Chat with us anytime"],
      link: "https://wa.me/12027042459",
    },
    {
      icon: Send,
      title: "Telegram",
      lines: ["(202) 704-2459", "Message us on Telegram"],
      link: "https://t.me/+12027042459",
    },
    {
      icon: Mail,
      title: "Email Us",
      lines: ["tax@goldspottax.com"],
      link: "mailto:tax@goldspottax.com",
    },
    {
      icon: Clock,
      title: "Business Hours",
      lines: ["Open 7 Days a Week", "9:00 AM - 6:00 PM"],
    },
  ];

  return (
    <div className="pt-20 min-h-screen" data-testid="contact-page">
      {/* Header */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-sm uppercase tracking-widest text-[#D4AF37]">Contact Us</span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mt-4 font-serif">
              Get in Touch
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mt-6">
              Have questions about our services? We're here to help. 
              Reach out and we'll respond within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="form-section" data-testid="contact-form-section">
              {submitted ? (
                <div className="text-center py-12 opacity-0 animate-fade-in-up" data-testid="contact-success">
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={32} className="text-black" />
                  </div>
                  <h3 className="text-2xl font-semibold font-serif mb-4">Message Sent!</h3>
                  <p className="text-muted-foreground">
                    Thank you for contacting us. We'll get back to you within 24 hours.
                  </p>
                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: "", email: "", phone: "", subject: "", message: "", smsOptIn: false });
                    }}
                    variant="outline"
                    className="mt-6 border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm"
                    data-testid="send-another-btn"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold mb-6 font-serif">Send us a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder="John Doe"
                          className="bg-[#111111] border-white/10 h-14 mt-2"
                          data-testid="contact-input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="john@example.com"
                          className="bg-[#111111] border-white/10 h-14 mt-2"
                          data-testid="contact-input-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(555) 123-4567"
                          className="bg-[#111111] border-white/10 h-14 mt-2"
                          data-testid="contact-input-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                          placeholder="How can we help?"
                          className="bg-[#111111] border-white/10 h-14 mt-2"
                          data-testid="contact-input-subject"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        placeholder="Tell us more about your inquiry..."
                        className="bg-[#111111] border-white/10 min-h-[160px] mt-2"
                        data-testid="contact-input-message"
                      />
                    </div>

                    {/* SMS Opt-In Checkbox */}
                    <div className="bg-[#111111] border border-white/10 p-4 rounded-sm space-y-4">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="smsOptIn"
                          checked={formData.smsOptIn}
                          onCheckedChange={(checked) => 
                            setFormData(prev => ({ ...prev, smsOptIn: checked }))
                          }
                          required
                          className="mt-1 border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                          data-testid="sms-opt-in-checkbox"
                        />
                        <Label 
                          htmlFor="smsOptIn" 
                          className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                        >
                          I agree to receive SMS/text messages and emails from Gold Spot Tax at 202-704-2459. 
                          Message and data rates may apply. Message frequency varies.
                        </Label>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        By submitting this form, you agree to our{" "}
                        <Link 
                          to="/terms-of-service" 
                          className="text-[#D4AF37] hover:underline"
                          target="_blank"
                        >
                          Terms of Service
                        </Link>
                        {" "}and{" "}
                        <Link 
                          to="/privacy-policy" 
                          className="text-[#D4AF37] hover:underline"
                          target="_blank"
                        >
                          Privacy Policy
                        </Link>.
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#D4AF37] text-black hover:bg-[#C5A059] font-semibold py-6 rounded-sm disabled:opacity-50"
                      data-testid="contact-submit-btn"
                    >
                      {submitting ? (
                        "Sending..."
                      ) : (
                        <>
                          Send Message
                          <Send size={18} className="ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-8" data-testid="contact-info-section">
              <h2 className="text-2xl font-semibold font-serif">Contact Information</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {contactInfo.map((info, index) => (
                  <div 
                    key={info.title}
                    className="bg-[#0A0A0A] border border-white/5 p-6 opacity-0 animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`contact-info-${info.title.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <info.icon className="w-8 h-8 text-[#D4AF37] mb-4" strokeWidth={1.5} />
                    <h3 className="font-semibold mb-2">{info.title}</h3>
                    {info.lines.map((line, i) => (
                      info.link && i === 0 ? (
                        <a 
                          key={i}
                          href={info.link}
                          className="block text-muted-foreground text-sm hover:text-[#D4AF37] transition-colors"
                        >
                          {line}
                        </a>
                      ) : (
                        <p key={i} className="text-muted-foreground text-sm">{line}</p>
                      )
                    ))}
                  </div>
                ))}
              </div>

              {/* Google Maps */}
              <div className="bg-[#0A0A0A] border border-white/5 p-8 mt-8">
                <h3 className="font-semibold mb-4 font-serif">Our Location</h3>
                <div className="aspect-video border border-white/5 overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3104.5!2d-77.0145!3d38.9615!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7c815b8d4a1a1%3A0x1234567890abcdef!2s738%20Longfellow%20St%20NW%2C%20Washington%2C%20DC!5e0!3m2!1sen!2sus!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Goldspot Tax Service Location"
                    data-testid="google-map"
                  />
                </div>
                <a 
                  href="https://www.google.com/maps/search/?api=1&query=738+Longfellow+St+NW+Unit+107+Washington+DC"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 text-[#D4AF37] hover:underline text-sm"
                  data-testid="get-directions-link"
                >
                  <MapPin size={16} />
                  Get Directions
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
